import {INDEX_MAP_SRC, MAP_GRID, parseMapId} from "../../../shared/maps/mbluIndexMap";
import {HeaderToggleButton} from "../../../shared/ui/overlay/HeaderToggleButton";
import {ReportPage, ReportChapterHeader} from "../../../shared/ui/layout/ReportPage";
import {shouldOpenSharedSection} from '../../../shared/navigation/useSharedReport';
import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, useTexture } from "@react-three/drei";
import {
  ArrowPathIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from "@heroicons/react/24/outline";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";

import townFairBanner from '../images/town-fair-banner.png';
import playgroundBanner from '../images/playground-banner.png';
import fountainBanner from '../images/fountain-banner.png';

const MAP_MODES = [
  { id: "default", label: "Default Map" },
  { id: "tax-density-2d", label: "2D Tax Density Map" },
  { id: "tax-density-3d", label: "3D Tax Density Map" },
];
const MAP_ZOOM = {
  min: 1,
  max: 3,
  step: 0.25,
};
const THREE_MAP = {
  width: 18,
  height: 16,
  maxColumnHeight: 8,
};
const THREE_ORBIT_CONTROLS = {
  minDistance: 2.2,
  maxDistance: 34,
  maxPolarAngle: Math.PI / 2.08,
};
const DENSITY_STOPS = [
  { at: 0, color: [21, 128, 61] },
  { at: 0.35, color: [163, 230, 53] },
  { at: 0.55, color: [250, 204, 21] },
  { at: 0.72, color: [249, 115, 22] },
  { at: 0.88, color: [220, 38, 38] },
  { at: 1, color: [126, 34, 206] },
];
const MBLU_TABLE_COLUMNS = [
  {
    key: "label",
    label: "MBLU",
    align: "left",
    headerAlign: "left",
    style: { width: "8rem", maxWidth: "8rem" },
  },
  {
    key: "appraisedValueTotal",
    label: "Appraised Total",
    format: "currency",
    align: "right",
    headerAlign: "right",
  },
  {
    key: "assessedValueTotal",
    label: "Assessed Total",
    format: "currency",
    align: "right",
    headerAlign: "right",
  },
  {
    key: "parcelCount",
    label: "Parcel Count",
    align: "right",
    headerAlign: "right",
  },
  {
    key: "assessedValueAverage",
    label: "Avg Assessed",
    format: "currency",
    align: "right",
    headerAlign: "right",
  },
  {
    key: "appraisedValueAverage",
    label: "Avg Appraised",
    format: "currency",
    align: "right",
    headerAlign: "right",
  },
];

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function interpolateColor(left, right, amount) {
  return left.map((channel, index) =>
    Math.round(channel + (right[index] - channel) * amount)
  );
}

function colorForPercentile(percentile, alpha = 0.48) {
  const [red, green, blue] = colorChannelsForPercentile(percentile);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function colorChannelsForPercentile(percentile) {
  const value = Math.max(0, Math.min(1, percentile));
  const upperIndex = DENSITY_STOPS.findIndex((stop) => value <= stop.at);
  const upper = DENSITY_STOPS[upperIndex === -1 ? DENSITY_STOPS.length - 1 : upperIndex];
  const lower = DENSITY_STOPS[Math.max(0, (upperIndex === -1 ? DENSITY_STOPS.length - 1 : upperIndex) - 1)];
  const span = upper.at - lower.at || 1;
  const amount = (value - lower.at) / span;
  return interpolateColor(lower.color, upper.color, amount);
}

function colorHexForPercentile(percentile) {
  const [red, green, blue] = colorChannelsForPercentile(percentile);
  return `#${[red, green, blue]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function percent(value) {
  return `${value.toFixed(2)}%`;
}

function clampMapZoom(value) {
  return Math.max(MAP_ZOOM.min, Math.min(MAP_ZOOM.max, value));
}

function rankedMapDocs(mapDocs) {
  const sortedValues = Array.from(
    new Set(
      mapDocs
        .map((doc) => Number(doc.assessedValueTotal || 0))
        .filter((value) => Number.isFinite(value))
        .sort((left, right) => left - right)
    )
  );
  const denominator = Math.max(1, sortedValues.length - 1);

  return mapDocs
    .map((doc) => {
      const parsed = parseMapId(doc.mapId || doc.id);
      if (!parsed) return null;

      const assessedValueTotal = Number(doc.assessedValueTotal || 0);
      const valueRank = Math.max(0, sortedValues.indexOf(assessedValueTotal));
      const percentile = valueRank / denominator;

      return {
        parsed,
        mapId: doc.mapId || doc.id,
        id: doc.mapId || doc.id,
        assessedValueTotal,
        appraisedValueTotal: Number(doc.appraisedValueTotal || 0),
        parcelCount: Number(doc.parcelCount || 0),
        blockCount: Number(doc.blockCount || 0),
        percentile,
      };
    })
    .filter(Boolean);
}

function buildDensityCells(mapDocs) {
  return rankedMapDocs(mapDocs)
    .map((doc) => {
      const { parsed } = doc;

      return {
        ...doc,
        style: {
          left: percent(MAP_GRID.left + parsed.columnIndex * MAP_GRID.cellWidth),
          top: percent(MAP_GRID.top + parsed.rowIndex * MAP_GRID.cellHeight),
          width: percent(MAP_GRID.cellWidth),
          height: percent(MAP_GRID.cellHeight),
          backgroundColor: colorForPercentile(doc.percentile),
        },
      };
    })
    .filter(Boolean);
}

function build3dDensityColumns(mapDocs) {
  const rankedDocs = rankedMapDocs(mapDocs);
  const maxAssessedValue = Math.max(
    1,
    ...rankedDocs.map((doc) => Number(doc.assessedValueTotal || 0))
  );

  return rankedDocs.map((doc) => {
    const left = MAP_GRID.left + doc.parsed.columnIndex * MAP_GRID.cellWidth;
    const top = MAP_GRID.top + doc.parsed.rowIndex * MAP_GRID.cellHeight;
    const centerX = ((left + MAP_GRID.cellWidth / 2) / 100 - 0.5) * THREE_MAP.width;
    const centerZ = ((top + MAP_GRID.cellHeight / 2) / 100 - 0.5) * THREE_MAP.height;
    const height =
      (Number(doc.assessedValueTotal || 0) / maxAssessedValue) *
      THREE_MAP.maxColumnHeight;

    return {
      ...doc,
      color: colorHexForPercentile(doc.percentile),
      height,
      position: [centerX, height / 2, centerZ],
      size: [
        (MAP_GRID.cellWidth / 100) * THREE_MAP.width * 0.94,
        Math.max(height, 0.02),
        (MAP_GRID.cellHeight / 100) * THREE_MAP.height * 0.94,
      ],
    };
  });
}

function buildMbluTableRows(mapDocs) {
  return rankedMapDocs(mapDocs).map((doc) => ({
    id: doc.id,
    label: doc.mapId,
    appraisedValueTotal: doc.appraisedValueTotal,
    assessedValueTotal: doc.assessedValueTotal,
    parcelCount: doc.parcelCount,
    assessedValueAverage: doc.parcelCount ?
      Math.round(doc.assessedValueTotal / doc.parcelCount) :
      0,
    appraisedValueAverage: doc.parcelCount ?
      Math.round(doc.appraisedValueTotal / doc.parcelCount) :
      0,
  }));
}

function TaxDensityScene3D({ columns }) {
  const texture = useTexture(INDEX_MAP_SRC);
  const { gl } = useThree();
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = Math.min(8, gl.capabilities.getMaxAnisotropy());

  return (
    <>
      <color attach="background" args={["#eef2f7"]} />
      <ambientLight intensity={1.7} />
      <directionalLight position={[8, 12, 8]} intensity={2.2} />
      <directionalLight position={[-8, 6, -6]} intensity={0.8} />

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
        <planeGeometry args={[THREE_MAP.width, THREE_MAP.height]} />
        <meshStandardMaterial map={texture} roughness={0.82} />
      </mesh>

      {columns.map((column) => (
        <mesh key={column.id} position={column.position} castShadow receiveShadow>
          <boxGeometry args={column.size} />
          <meshStandardMaterial
            color={column.color}
            transparent
            opacity={0.82}
            roughness={0.55}
            metalness={0.05}
          />
        </mesh>
      ))}

      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.08}
        minDistance={THREE_ORBIT_CONTROLS.minDistance}
        maxDistance={THREE_ORBIT_CONTROLS.maxDistance}
        maxPolarAngle={THREE_ORBIT_CONTROLS.maxPolarAngle}
        target={[0, 0.7, 0]}
      />
    </>
  );
}

function TaxDensityMap3D({ columns }) {
  return (
    <div
      className="h-[420px] w-full overflow-hidden rounded-md border border-gray-200 bg-slate-100 sm:h-[500px] lg:h-[560px]"
      role="img"
      aria-label="3D assessed value density map"
    >
      <Canvas
        shadows
        dpr={[1, 1.5]}
        gl={{
          antialias: true,
          powerPreference: "high-performance",
        }}
        camera={{
          position: [10.35, 13.8, 16.1],
          fov: 42,
          near: 0.1,
          far: 100,
        }}
      >
        <TaxDensityScene3D columns={columns} />
      </Canvas>
    </div>
  );
}

function MapZoomControls({ zoom, setZoom }) {
  const setClampedZoom = (value) => {
    setZoom(clampMapZoom(value));
  };

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-2"
      aria-label="Map zoom controls"
    >
      <button
        type="button"
        onClick={() => setClampedZoom(zoom - MAP_ZOOM.step)}
        className="inline-flex h-9 w-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Zoom out map"
        title="Zoom out"
        disabled={zoom <= MAP_ZOOM.min}
      >
        <MagnifyingGlassMinusIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <input
        type="range"
        min={MAP_ZOOM.min}
        max={MAP_ZOOM.max}
        step={MAP_ZOOM.step}
        value={zoom}
        onChange={(event) => setClampedZoom(Number(event.target.value))}
        className="h-2 w-44 accent-gray-900"
        aria-label="Map zoom level"
      />
      <button
        type="button"
        onClick={() => setClampedZoom(zoom + MAP_ZOOM.step)}
        className="inline-flex h-9 w-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Zoom in map"
        title="Zoom in"
        disabled={zoom >= MAP_ZOOM.max}
      >
        <MagnifyingGlassPlusIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => setClampedZoom(1)}
        className="inline-flex h-9 w-9 items-center justify-center rounded border border-gray-300 bg-white text-gray-800 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Reset map zoom"
        title="Reset zoom"
        disabled={zoom === 1}
      >
        <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <span className="min-w-12 text-sm font-semibold text-gray-700">
        {Math.round(zoom * 100)}%
      </span>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultCollapsed = true }) {
  const sectionId = `tax-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const [collapsed, setCollapsed] = useState(() => shouldOpenSharedSection(sectionId) ? false : defaultCollapsed);

  return (
    <section className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
      <HeaderToggleButton title={title} controlsId={sectionId} ariaLabel={title}
        isOpen={!collapsed} onToggle={() => setCollapsed(previous => !previous)} />
      {!collapsed && <div id={sectionId} className="bg-white p-4">{children}</div>}
    </section>
  );
}

export function TaxDensityMap() {
  const [selectedMapMode, setSelectedMapMode] = useState("tax-density-3d");
  const [mapZoom, setMapZoom] = useState(1);
  const [mapViewResetKey, setMapViewResetKey] = useState(0);
  const [mapDocs, setMapDocs] = useState([]);
  const [densityStatus, setDensityStatus] = useState("idle");
  const [densityError, setDensityError] = useState("");
  const is2dDensityMode = selectedMapMode === "tax-density-2d";
  const is3dDensityMode = selectedMapMode === "tax-density-3d";
  const isDensityMode = is2dDensityMode || is3dDensityMode;

  useEffect(() => {
    if (mapDocs.length > 0) {
      return;
    }

    let cancelled = false;

    async function loadDensityMapData() {
      setDensityStatus("loading");
      setDensityError("");

      try {
        const snapshot = await getDocs(collection(db, "propertyMbluMaps"));
        if (cancelled) return;

        setMapDocs(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setDensityStatus("loaded");
      } catch (error) {
        if (cancelled) return;

        setDensityError(error.message || "Unable to load tax density data.");
        setDensityStatus("error");
      }
    }

    loadDensityMapData();

    return () => {
      cancelled = true;
    };
  }, [mapDocs.length]);

  const densityCells = useMemo(() => buildDensityCells(mapDocs), [mapDocs]);
  const densityColumns3d = useMemo(
    () => build3dDensityColumns(mapDocs),
    [mapDocs]
  );
  const mbluTableRows = useMemo(() => buildMbluTableRows(mapDocs), [mapDocs]);
  const handleMapModeChange = (modeId) => {
    setSelectedMapMode(modeId);
    setMapZoom(1);
    setMapViewResetKey((currentKey) => currentKey + 1);
  };

  return (
    <ReportPage editorial title="Tax Density Map" banner={townFairBanner}
      bannerAlt="Watercolor illustration of a Glastonbury town fair at dusk"
      description="Explore Glastonbury’s property values by map area. Use the town index map to locate an area, switch to a 2D or 3D view to compare total assessed values, or open MBLU Values to see the underlying property counts and valuations. These views compare property values, not individual tax bills.">
        <ReportChapterHeader headingAs="h2" title="Glastonbury Index Map" tone="lavender"
          image={playgroundBanner} imageAlt="Watercolor illustration of a Glastonbury park playground"
          description="Find your way around the town’s assessment map areas. Open the map below to switch between the original index and the colored 2D or 3D assessed-value views. Use the zoom controls to take a closer look." />
        <CollapsibleSection title="Glastonbury Index Map" defaultCollapsed={false}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div
              className="inline-flex flex-wrap gap-1 rounded-md border border-gray-300 bg-gray-100 p-1"
              role="tablist"
              aria-label="Map display mode"
            >
              {MAP_MODES.map((mode) => {
                const isSelected = mode.id === selectedMapMode;

                return (
                  <button
                    key={mode.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    onClick={() => handleMapModeChange(mode.id)}
                    className={`rounded px-3 py-2 text-sm font-semibold transition ${
                      isSelected
                        ? "bg-gray-900 text-white shadow-sm"
                        : "bg-transparent text-gray-700 hover:bg-white"
                    }`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>

            {!is3dDensityMode && (
              <MapZoomControls zoom={mapZoom} setZoom={setMapZoom} />
            )}
          </div>

          {isDensityMode && densityStatus === "loading" && (
            <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">
              Loading tax density map...
            </div>
          )}

          {isDensityMode && densityStatus === "error" && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">
              {densityError}
            </div>
          )}

          {isDensityMode && densityCells.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-700">
              <span>Lower assessed value</span>
              <div
                className="h-3 w-48 rounded-full border border-gray-300"
                style={{
                  background:
                    "linear-gradient(90deg, #15803d 0%, #a3e635 35%, #facc15 55%, #f97316 72%, #dc2626 88%, #7e22ce 100%)",
                }}
              />
              <span>Higher assessed value</span>
            </div>
          )}

          {is3dDensityMode ? (
            densityColumns3d.length > 0 ? (
              <TaxDensityMap3D
                key={`tax-density-3d-${mapViewResetKey}`}
                columns={densityColumns3d}
              />
            ) : densityStatus === "loaded" ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
                No 3D tax density data found.
              </div>
            ) : null
          ) : (
              <div
                className="overflow-auto rounded-md border border-gray-200 bg-gray-50"
                data-testid="zoomable-map-viewport"
              >
                <div
                  className="relative"
                  data-testid="zoomable-map-layer"
                  style={{
                    width: `${mapZoom * 100}%`,
                  }}
                >
                  <img
                    src={INDEX_MAP_SRC}
                    alt="Town of Glastonbury index map showing MBLU map blocks"
                    className="block w-full h-auto"
                  />
                  {is2dDensityMode && densityCells.length > 0 && (
                    <div
                      className="absolute inset-0"
                      role="img"
                      aria-label="2D assessed value density overlay"
                    >
                      {densityCells.map((cell) => (
                        <div
                          key={cell.id}
                          className="absolute border border-gray-900/25"
                          style={cell.style}
                          title={`${cell.id}: ${formatCurrency(cell.assessedValueTotal)} assessed value`}
                          aria-label={`${cell.id} assessed value ${formatCurrency(cell.assessedValueTotal)}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
          )}
        </CollapsibleSection>

        <div className="mt-6">
          <ReportChapterHeader headingAs="h2" title="MBLU Values" tone="orange"
            image={fountainBanner} imageAlt="Watercolor illustration of a stone fountain in a Glastonbury park"
            description="MBLU stands for Map, Block, Lot, and Unit—the identifiers used for property records. The report below groups properties by map area and lists parcel counts, total appraised and assessed values, and average values. Open it to sort and compare areas." />
          <div className="mt-5">
          <CollapsibleSection title="MBLU Values" defaultCollapsed={false}>
            {densityStatus === "loading" && (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">
                Loading MBLU values...
              </div>
            )}

            {densityStatus === "error" && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">
                {densityError}
              </div>
            )}

            {densityStatus === "loaded" && mbluTableRows.length === 0 && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
                No MBLU values found.
              </div>
            )}

            {mbluTableRows.length > 0 && (
              <CommonTable
                title={null}
                columns={MBLU_TABLE_COLUMNS}
                data={mbluTableRows}
                isEditing={false}
                headerColor="bg-[#e5eef9]"
                showAggregateTotal
                defaultSortField="assessedValueTotal"
                defaultSortOrder="desc"
                filterEmptyRows={false}
              />
            )}
          </CollapsibleSection>
          </div>
        </div>

    </ReportPage>
  );
}

export {
  buildDensityCells,
  build3dDensityColumns,
  buildMbluTableRows,
  colorForPercentile,
  colorHexForPercentile,
  formatCurrency,
  percent,
  parseMapId,
  THREE_ORBIT_CONTROLS,
};
